import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToday } from '../../app/today';
import { useServices } from '../../app/services';
import { todayIso, type DebtSummary } from '../../data/store/store';
import { DEBT_TYPES, type BudgetMember, type Category, type RecurringTemplate, type Subcategory, type Transaction } from '../../domain/model';
import { billRows, isLastPeriod, type BillRow } from '../../domain/recurring';
import { formatDayMonth } from '../../i18n/format';
import type { ParsedAmount } from '../../i18n/parseAmount';
import { Amount, AmountInput, BottomSheet, Button, CategoryDot, series, useFormat, useToast } from '../../ui';
import './recurring.css';

export interface BillsData {
  today: string;
  rows: BillRow[];
  templates: RecurringTemplate[];
  categories: Map<string, Category>;
  subcategories: Map<string, Subcategory>;
  members: BudgetMember[];
  me: BudgetMember | null;
  /** What is known about each loan / card bill (remaining, installments left). */
  debts: Map<string, DebtSummary>;
  /** Entries recorded against bills, two calendar years back (for "last time" and yearly costs). */
  linked: Transaction[];
}

/** This period's bills of the active budget, refreshed on every data change. */
export function useBills(): BillsData | null {
  const { store, activeSpace, identity, dataVersion } = useServices();
  const today = useToday();
  const [data, setData] = useState<BillsData | null>(null);
  useEffect(() => {
    if (!activeSpace) return;
    let live = true;
    // Two calendar years back is enough to find "last time" for yearly bills too.
    const from = `${Number(today.slice(0, 4)) - 2}-01-01`;
    void Promise.all([
      store.recurring.listForSpace(activeSpace.id),
      store.recurring.linkedEntries(activeSpace.id, from, '9999-12-31'),
      store.categories.list({ spaceId: activeSpace.id }, { includeDeleted: true }),
      store.subcategories.list({ spaceId: activeSpace.id }, { includeDeleted: true }),
      store.members.list({ spaceId: activeSpace.id }, { orderBy: 'created_at' }),
      store.members.forUser(activeSpace.id, identity.session.userId),
    ]).then(async ([templates, linked, cats, subs, members, me]) => {
      const debts = new Map<string, DebtSummary>();
      for (const tpl of templates.filter((x) => x.active && DEBT_TYPES.includes(x.type))) {
        debts.set(tpl.id, await store.debt.summaryFor(activeSpace.id, { recurringTemplateId: tpl.id }));
      }
      if (!live) return;
      setData({
        today,
        rows: billRows(templates, linked, today),
        templates,
        categories: new Map(cats.map((c) => [c.id, c])),
        subcategories: new Map(subs.map((s) => [s.id, s])),
        members,
        me: me ?? members[0] ?? null,
        debts,
        linked,
      });
    });
    return () => {
      live = false;
    };
  }, [store, activeSpace, identity, dataVersion, today]);
  return data;
}

const isOpen = (r: BillRow) => r.status !== 'recorded';

/**
 * Recording bills: one tap for a fixed bill ("Same as last month"), an
 * amount sheet for a variable one (last month shown, one tap to repeat it).
 * The sheet focuses the amount inside the tap that opens it, so the
 * keypad is up at once on phones.
 */
export function useRecordBill(data: BillsData | null) {
  const { t } = useTranslation();
  const { store, language, notifyDataChanged } = useServices();
  const { money } = useFormat();
  const toast = useToast();
  const [row, setRow] = useState<BillRow | null>(null);
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<ParsedAmount>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);

  const undo = (ids: string[]) => ({
    label: t('quickAdd.undo'),
    onClick: () => {
      void Promise.all(ids.map((id) => store.transactions.remove(id, data?.me?.id ?? null))).then(() => {
        notifyDataChanged();
        toast.show({ message: t('quickAdd.removed') });
      });
    },
  });

  async function record(target: BillRow, amountMinor?: number): Promise<boolean> {
    if (!data?.me) return false;
    try {
      const tx = await store.recurring.record(target.template.id, {
        addedBy: data.me.id,
        amountMinor,
      });
      notifyDataChanged();
      toast.show({
        message: t('recurring.recorded', {
          name: target.template.name,
          amount: money(tx.amountMinor, { currency: tx.currency }),
        }),
        action: undo([tx.id]),
      });
      return true;
    } catch (e) {
      console.error('[recurring]', e);
      toast.show({ message: t('recurring.error') });
      return false;
    }
  }

  async function recordAll(targets: BillRow[]) {
    if (!data?.me || targets.length === 0) return;
    const ids: string[] = [];
    try {
      for (const r of targets) ids.push((await store.recurring.record(r.template.id, { addedBy: data.me.id })).id);
      toast.show({
        message: t('recurring.recordedAll', { count: ids.length }),
        action: undo(ids),
      });
    } catch (e) {
      console.error('[recurring]', e);
      toast.show({ message: t('recurring.error') });
    } finally {
      notifyDataChanged();
    }
  }

  function open(target: BillRow) {
    flushSync(() => {
      setRow(target);
      setText('');
      setParsed(null);
      setTouched(false);
      setFailed(false);
    });
    amountRef.current?.focus({ preventScroll: true });
  }

  const close = () => setRow(null);

  async function save(amountOverride?: number) {
    if (!row || busy) return;
    const amount = amountOverride ?? (typeof parsed === 'number' ? parsed : null);
    if (!amount || amount <= 0) {
      setTouched(true);
      amountRef.current?.focus();
      return;
    }
    setBusy(true);
    setFailed(false);
    const ok = await record(row, amount);
    setBusy(false);
    if (ok) close();
    else setFailed(true);
  }

  const previousHint = (r: BillRow) => {
    if (!r.previous) return undefined;
    const amount = money(r.previous.amountMinor, {
      currency: r.previous.currency,
    });
    return isLastPeriod(r, data?.today ?? todayIso())
      ? t('recurring.lastMonth', { amount })
      : t('recurring.lastTime', {
          amount,
          date: formatDayMonth(r.previous.date, language),
        });
  };

  const repeatMinor = row ? (row.template.kind === 'fixed' ? row.suggestedMinor : (row.previous?.amountMinor ?? null)) : null;
  const repeatLabel =
    row && repeatMinor !== null
      ? row.template.kind === 'fixed' || isLastPeriod(row, data?.today ?? todayIso())
        ? t('recurring.sameAsLastMonthAmount', {
            amount: money(repeatMinor, { currency: row.template.currency }),
          })
        : t('recurring.sameAsLastTimeAmount', {
            amount: money(repeatMinor, { currency: row.template.currency }),
          })
      : null;

  const sheet = (
    <BottomSheet
      open={row !== null}
      onClose={close}
      title={row?.template.name ?? ''}
      footer={
        <Button variant="primary" size="lg" block loading={busy} onClick={() => void save()}>
          {t('quickAdd.save')}
        </Button>
      }
    >
      {row && (
        <div className="stack">
          <div className="qa-amount">
            <AmountInput
              label={t('quickAdd.amount')}
              value={text}
              currency={row.template.currency}
              inputRef={amountRef}
              onValueChange={(v, p) => {
                setText(v);
                setParsed(p);
              }}
              onSubmit={() => void save()}
              hint={row.template.kind === 'fixed' ? t('recurring.fixedHint') : previousHint(row)}
              error={
                parsed === 'invalid'
                  ? t('quickAdd.invalid')
                  : touched && (parsed === null || parsed === 0)
                    ? t('quickAdd.required')
                    : undefined
              }
            />
            {repeatLabel && !text && (
              <Button variant="secondary" onClick={() => void save(repeatMinor!)}>
                {repeatLabel}
              </Button>
            )}
          </div>
          {failed && (
            <p className="field-msg field-error" role="alert">
              {t('recurring.error')}
            </p>
          )}
        </div>
      )}
    </BottomSheet>
  );

  return { record, recordAll, open, sheet, previousHint };
}

/**
 * The bills as a timetable: what is due (and when), what is done. A fixed
 * bill carries its one-tap action; a variable one its last amount and
 * "Enter amount".
 */
export function BillList({
  data,
  rows,
  recorder,
  onEdit,
}: {
  data: BillsData;
  rows: BillRow[];
  recorder: ReturnType<typeof useRecordBill>;
  onEdit?: (template: RecurringTemplate) => void;
}) {
  const { t } = useTranslation();
  const { language } = useServices();
  const { money } = useFormat();
  const [pending, setPending] = useState<string | null>(null);

  const status = (r: BillRow) => {
    if (r.status === 'recorded')
      return t('recurring.recordedOn', {
        date: formatDayMonth(r.recorded[0]!.date, language),
      });
    if (!r.dueDate) return null;
    return r.status === 'overdue'
      ? t('recurring.overdue', { date: formatDayMonth(r.dueDate, language) })
      : t('recurring.dueOn', { date: formatDayMonth(r.dueDate, language) });
  };

  return (
    <ul className="list list-divided bills" aria-label={t('recurring.listLabel')}>
      {rows.map((r) => {
        const tpl = r.template;
        const color = series(data.categories.get(tpl.categoryId)?.colorSlot ?? 0);
        const when = status(r);
        const debt = data.debts.get(tpl.id);
        const debtNote = [
          debt?.installmentsLeft ? t('recurring.installmentsLeft', { count: debt.installmentsLeft.value }) : null,
          debt?.remainingBalanceMinor ? t('recurring.remaining', { amount: money(debt.remainingBalanceMinor.value, { currency: tpl.currency, whole: true }) }) : null,
        ];
        const recordedMinor = r.recorded.reduce((s, e) => s + e.amountMinor, 0);
        const shownMinor = r.status === 'recorded' ? recordedMinor : tpl.kind === 'fixed' ? r.suggestedMinor : null;
        const main = (
          <>
            <span className="row-leading">
              <CategoryDot color={color} />
            </span>
            <span className="row-main">
              <span className="row-title">
                <span className="row-title-text">{tpl.name}</span>
              </span>
              <span className={r.status === 'overdue' ? 'row-subtitle tone-up' : 'row-subtitle'}>
                {[tpl.kind === 'fixed' ? t('recurring.fixed') : t('recurring.variable'), when, ...debtNote].filter(Boolean).join(' · ')}
              </span>
            </span>
            {shownMinor !== null && (
              <span className="row-trailing num">
                <Amount minor={shownMinor} currency={tpl.currency} tone={r.status === 'recorded' ? 'muted' : 'default'} />
              </span>
            )}
          </>
        );
        return (
          <li key={tpl.id} className={`list-item bill bill-${r.status}`}>
            {onEdit ? (
              <button
                type="button"
                className="row row-regular row-interactive"
                onClick={() => onEdit(tpl)}
                aria-label={`${tpl.name}, ${t('recurring.edit')}`}
              >
                {main}
              </button>
            ) : (
              <div className="row row-regular">{main}</div>
            )}
            {isOpen(r) && (
              <div className="bill-actions">
                {tpl.kind === 'fixed' ? (
                  <>
                    <Button
                      variant="secondary"
                      loading={pending === tpl.id}
                      aria-label={`${t('recurring.sameAsLastMonth')}, ${tpl.name}`}
                      onClick={() => {
                        setPending(tpl.id);
                        void recorder.record(r).finally(() => setPending(null));
                      }}
                    >
                      {t('recurring.sameAsLastMonth')}
                    </Button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => recorder.open(r)}
                      aria-label={`${t('recurring.enterAmount')}, ${tpl.name}`}
                    >
                      {t('recurring.enterAmount')}
                    </button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" onClick={() => recorder.open(r)} aria-label={`${t('recurring.enterAmount')}, ${tpl.name}`}>
                      {t('recurring.enterAmount')}
                    </Button>
                    <span className="bill-last tone-muted">{recorder.previousHint(r) ?? t('recurring.noHistory')}</span>
                  </>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Fixed bills still open this period: what "Record all" would record. */
export function openFixed(rows: BillRow[]) {
  return rows.filter((r) => isOpen(r) && r.template.kind === 'fixed' && r.suggestedMinor !== null);
}

export function openRows(rows: BillRow[]) {
  return rows.filter(isOpen);
}
